// src/components/programs/ProgramForm/Step0ProgramInfo.jsx

import React, { useCallback, useState } from "react";
import { Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails, Fade } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LanguageToggle from "../../global/LanguageToggle";
import toast from "react-hot-toast";

// Import hooks
import useGetPaxTypesForSelect from "../../../hooks/paxTypes/useGetPaxTypesForSelect";
import useGetCitiesForSelect from "../../../hooks/cities/useGetCitiesForSelect";
import useGetAgentsForSelect from "../../../hooks/agents/useGetAgentsForSelect";
import { useUploadMedia } from "../../../hooks/media/useUploadMedia";
// useFormLanguage is provided from parent wrapper via props

// Import helpers
import { handleMultiLangChange as multiLangHelper } from "../../../utils/forms/multiLangHelper";
import { createAccordionHandler, isPanelExpanded } from "../../../utils/forms/accordionHelper";
import { isValidImageType, isValidFileSize } from "../../../utils/forms/validationHelper";
import { calculateDuration, generateDayBoxes } from "../../../utils/programs/dateCalculations";
import { transformOptionsToIdName } from "../../../utils/forms/transformHelper";

// Import sections
import BasicInfoSection from "./Step0Sections/BasicInfoSection";
import DateRangeSection from "./Step0Sections/DateRangeSection";
import PaxTypesSection from "./Step0Sections/PaxTypesSection";
import CoverImageSection from "./Step0Sections/CoverImageSection";

const Step0ProgramInfo = ({
    formData,
    setFormData,
    errors = {},
    isEdit = false,
    currentData = null,
    onClearErrors = null, // Callback to clear errors in parent
    activeLang = 'en',
    setActiveLang = () => { },
    isArabic = false,
}) => {

    // Track local error state to clear UI errors immediately
    const [localClearedErrors, setLocalClearedErrors] = useState({});

    // Helper function to check if section has errors
    const getSectionErrors = useCallback((sectionName) => {
        const sectionErrorMap = {
            basic: [
                'title_ar', 'title_en',
                'description_ar', 'description_en',
                'agent_id',
                'city_ids'
            ], // BasicInfoSection
            dates: ['start_date', 'end_date'], // DateRangeSection
            pax: ['pax_types'], // PaxTypesSection
            cover: ['cover_image', 'coverImageError'], // CoverImageSection
        };

        const relevantKeys = sectionErrorMap[sectionName] || [];
        // Check actual errors, excluding cleared ones
        return relevantKeys.some(key => {
            // If error was cleared locally, don't show it
            if (localClearedErrors[key]) return false;
            return errors[key];
        });
    }, [errors, localClearedErrors]);

    // Get error status for each section - memoized to avoid object recreation
    const hasSectionError = React.useMemo(() => ({
        basic: getSectionErrors('basic'),
        dates: getSectionErrors('dates'),
        pax: getSectionErrors('pax'),
        cover: getSectionErrors('cover'),
    }), [getSectionErrors]);

    // Accordion state
    const [expandedPanels, setExpandedPanels] = useState(['panel1']);
    const handleAccordionChange = createAccordionHandler(setExpandedPanels, true);

    // Auto-expand accordion with errors
    React.useEffect(() => {
        const panelsToExpand = [];

        if (hasSectionError.basic && !expandedPanels.includes('panel1')) {
            panelsToExpand.push('panel1');
        }
        if (hasSectionError.dates && !expandedPanels.includes('panel2')) {
            panelsToExpand.push('panel2');
        }
        if (hasSectionError.pax && !expandedPanels.includes('panel3')) {
            panelsToExpand.push('panel3');
        }
        if (hasSectionError.cover && !expandedPanels.includes('panel4')) {
            panelsToExpand.push('panel4');
        }

        if (panelsToExpand.length > 0) {
            setExpandedPanels(prev => [...new Set([...prev, ...panelsToExpand])]);
        }
    }, [expandedPanels, hasSectionError]);

    // Upload media hook
    const { uploadMedia, isPending: isUploadingCover } = useUploadMedia();

    // Get pax types
    const { options: paxTypesOptions, isLoading: loadingPaxTypes } = useGetPaxTypesForSelect();

    // Get cities
    const { options: cities, isLoading: loadingCities } = useGetCitiesForSelect();
    const citiesOptions = React.useMemo(() => transformOptionsToIdName(cities), [cities]);

    // Get agents
    const { agentsOptions, isLoading: loadingAgents } = useGetAgentsForSelect();
    const formattedAgentsOptions = React.useMemo(() => transformOptionsToIdName(agentsOptions), [agentsOptions]);

    // Helper function to clear specific errors
    // This creates a new errors object without the specified fields
    // The parent component should handle this signal and update its errors state
    const clearErrorsForFields = useCallback((fieldNames) => {
        // Mark these fields as cleared locally for immediate UI feedback
        const cleared = {};
        fieldNames.forEach(field => {
            cleared[field] = true;
        });
        setLocalClearedErrors(prev => ({
            ...prev,
            ...cleared
        }));

        // Call parent callback to remove errors from parent component
        if (onClearErrors) {
            onClearErrors(fieldNames);
        }
    }, [onClearErrors]);

    // استخدام Multi-lang Helper with error clearing
    const handleFieldChange = useCallback((field, lang, value) => {
        let processedValue = value;
        // Capitalize Program Name (title) in English
        if (field === 'title' && lang === 'en' && value) {
            processedValue = value
                .split(" ")
                .map(word => {
                    if (!word) return "";
                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                })
                .join(" ");
        }

        multiLangHelper(setFormData, formData, field, lang, processedValue);
        // Clear the specific language error for this field when user starts editing
        clearErrorsForFields([`${field}_${lang}`]);
    }, [formData, setFormData, clearErrorsForFields]);

    // ✅ Handler for flat (non-translatable) fields like agent_id, status, etc.
    const handleFlatFieldChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        clearErrorsForFields([field]);
    }, [setFormData, clearErrorsForFields]);

    // Handle date change
    const handleDateChange = useCallback((field, value) => {
        const newStartDate = field === "start_date" ? value : formData.start_date;
        const newEndDate = field === "end_date" ? value : formData.end_date;

        if (newStartDate && newEndDate) {
            const { durations, nights } = calculateDuration(newStartDate, newEndDate);

            // Generate day boxes
            const existingDays = formData.itinerary;
            const newDays = generateDayBoxes(newStartDate, durations);

            // Preserve existing data
            const preservedDays = newDays.map((newDay, index) => {
                if (existingDays[index]) {
                    // ✅ FIX: Clamp per_night products' nights to new maximum when days are reduced
                    const adjustedProducts = existingDays[index].products.map(product => {
                        const pricingUnit = product.product_snapshot?.pricing_unit;
                        if (
                            pricingUnit === 'per_night' &&
                            product.dynamic_fields?.nights != null &&
                            product.dynamic_fields.nights > nights
                        ) {
                            return {
                                ...product,
                                dynamic_fields: {
                                    ...product.dynamic_fields,
                                    nights: nights,
                                },
                            };
                        }
                        return product;
                    });

                    return {
                        ...newDay,
                        day_name: existingDays[index].day_name,
                        products: adjustedProducts,
                    };
                }
                return newDay;
            });

            setFormData({
                ...formData,
                [field]: value,
                durations_days: durations,
                durations_nights: nights,
                itinerary: preservedDays,
            });
        } else {
            setFormData({
                ...formData,
                [field]: value,
            });
        }

        // Clear date errors when user changes dates
        clearErrorsForFields(['start_date', 'end_date']);
    }, [formData, setFormData, clearErrorsForFields]);

    // Handle cover image change
    const handleCoverImageChange = useCallback((event) => {
        const file = event.target.files?.[0];

        setFormData(prev => ({
            ...prev,
            coverImageError: '',
            uploadingCoverImage: false,
            coverImageSuccess: ''
        }));

        // Clear cover image errors when user selects a file
        clearErrorsForFields(['cover_image', 'coverImageError']);

        if (!file) return;

        if (!isValidImageType(file)) {
            const errorMsg = isArabic
                ? 'يرجى اختيار صورة بصيغة صحيحة (JPG, PNG, WEBP)'
                : 'Please select a valid image format (JPG, PNG, WEBP)';

            setFormData(prev => ({ ...prev, coverImageError: errorMsg }));
            toast.error(errorMsg);
            return;
        }

        if (!isValidFileSize(file, 5)) {
            const errorMsg = isArabic
                ? 'حجم الصورة يجب أن لا يتجاوز 5 ميجابايت'
                : 'Image size must not exceed 5MB';

            setFormData(prev => ({ ...prev, coverImageError: errorMsg }));
            toast.error(errorMsg);
            return;
        }

        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        setFormData(prev => ({
            ...prev,
            cover_image_preview: URL.createObjectURL(file),
            uploadingCoverImage: true
        }));

        uploadMedia(uploadFormData, {
            onSuccess: (res) => {
                const fileName = res?.data?.file_name || res?.data?.name || res?.file_name || '';

                if (!fileName) {
                    const errorMsg = isArabic
                        ? 'تعذر الحصول على اسم الملف من الخادم'
                        : 'Cannot get uploaded file name from server';

                    setFormData(prev => ({
                        ...prev,
                        uploadingCoverImage: false,
                        coverImageError: errorMsg
                    }));
                    toast.error(errorMsg);
                    return;
                }

                setFormData(prev => ({
                    ...prev,
                    cover_image: fileName,
                    uploadingCoverImage: false,
                    coverImageSuccess: isArabic ? 'تم رفع الصورة بنجاح' : 'Image uploaded successfully',
                    coverImageError: ''
                }));

                // Clear cover image errors on successful upload
                clearErrorsForFields(['cover_image', 'coverImageError']);

                toast.success(
                    isArabic ? 'تم رفع صورة الغلاف بنجاح' : 'Cover image uploaded successfully'
                );
            },
            onError: (error) => {
                console.error('Cover image upload error:', error);

                const errorMessage = error?.response?.data?.message ||
                    error?.message ||
                    (isArabic ? 'فشل رفع الصورة، الرجاء المحاولة مرة أخرى' : 'Failed to upload image, please try again');

                setFormData(prev => ({
                    ...prev,
                    uploadingCoverImage: false,
                    coverImageError: errorMessage,
                    coverImageSuccess: '',
                    cover_image: null,
                    cover_image_preview: ''
                }));

                toast.error(errorMessage);
            }
        });
    }, [isArabic, setFormData, clearErrorsForFields, uploadMedia]);
    // Handle city IDs change
    const handleCityIdsChange = useCallback((value) => {
        setFormData(prev => ({
            ...prev,
            city_ids: value
        }));
        // Clear city_ids error when user selects cities
        clearErrorsForFields(['city_ids']);
    }, [setFormData, clearErrorsForFields]);


    // Shared props
    const commonProps = {
        formData,
        setFormData,
        errors,
        isArabic,
        activeLang,
        handleMultiLangChange: handleFieldChange,
        onClearError: clearErrorsForFields, // Pass error clearing function
    };

    return (
        <Paper sx={{ p: 4 }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                        {isArabic ? 'معلومات البرنامج' : 'Program Information'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isArabic
                            ? 'أدخل المعلومات الأساسية للبرنامج'
                            : 'Enter basic program information'}
                    </Typography>
                </Box>

                <LanguageToggle
                    activeLang={activeLang}
                    onChange={(lang) => setActiveLang(lang)}
                    showLabel={true}
                />
            </Box>

            {/* Accordions */}
            <Box>
                {/* 1. Basic Info */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel1')}
                    onChange={handleAccordionChange('panel1')}
                    sx={{
                        mb: 2,
                        border: hasSectionError.basic ? '2px solid #ef5350' : '1px solid #e0e0e0',
                        backgroundColor: hasSectionError.basic ? 'rgba(239, 83, 80, 0.05)' : 'transparent',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            borderColor: hasSectionError.basic ? '#ef5350' : '#e0e0e0',
                        }
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '📋 المعلومات الأساسية' : '📋 Basic Information'}
                        </Typography>
                        <Fade in={hasSectionError.basic} timeout={300}>
                            <Typography
                                sx={{
                                    ml: 1,
                                    color: '#ef5350',
                                    fontSize: '0.85rem',
                                    fontWeight: 600
                                }}
                            >
                                {isArabic ? '⚠️ يوجد أخطاء' : '⚠️ Errors'}
                            </Typography>
                        </Fade>
                    </AccordionSummary>
                    <AccordionDetails>
                        <BasicInfoSection
                            {...commonProps}
                            citiesOptions={citiesOptions}
                            loadingCities={loadingCities}
                            onCityIdsChange={handleCityIdsChange}
                            agentsOptions={formattedAgentsOptions}
                            loadingAgents={loadingAgents}
                            onFlatFieldChange={handleFlatFieldChange}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* 2. Date Range */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel2')}
                    onChange={handleAccordionChange('panel2')}
                    sx={{
                        mb: 2,
                        border: hasSectionError.dates ? '2px solid #ef5350' : '1px solid #e0e0e0',
                        backgroundColor: hasSectionError.dates ? 'rgba(239, 83, 80, 0.05)' : 'transparent',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            borderColor: hasSectionError.dates ? '#ef5350' : '#e0e0e0',
                        }
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '📅 التواريخ' : '📅 Date Range'}
                        </Typography>
                        <Fade in={hasSectionError.dates} timeout={300}>
                            <Typography
                                sx={{
                                    ml: 1,
                                    color: '#ef5350',
                                    fontSize: '0.85rem',
                                    fontWeight: 600
                                }}
                            >
                                {isArabic ? '⚠️ يوجد أخطاء' : '⚠️ Errors'}
                            </Typography>
                        </Fade>
                    </AccordionSummary>
                    <AccordionDetails>
                        <DateRangeSection
                            {...commonProps}
                            handleDateChange={handleDateChange}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* 3. Pax Types */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel3')}
                    onChange={handleAccordionChange('panel3')}
                    sx={{
                        mb: 2,
                        border: hasSectionError.pax ? '2px solid #ef5350' : '1px solid #e0e0e0',
                        backgroundColor: hasSectionError.pax ? 'rgba(239, 83, 80, 0.05)' : 'transparent',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            borderColor: hasSectionError.pax ? '#ef5350' : '#e0e0e0',
                        }
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '👥 أنواع الركاب' : '👥 Passenger Types'}
                        </Typography>
                        <Fade in={hasSectionError.pax} timeout={300}>
                            <Typography
                                sx={{
                                    ml: 1,
                                    color: '#ef5350',
                                    fontSize: '0.85rem',
                                    fontWeight: 600
                                }}
                            >
                                {isArabic ? '⚠️ يوجد أخطاء' : '⚠️ Errors'}
                            </Typography>
                        </Fade>
                    </AccordionSummary>
                    <AccordionDetails>
                        <PaxTypesSection
                            {...commonProps}
                            paxTypesOptions={paxTypesOptions}
                            loadingPaxTypes={loadingPaxTypes}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* 4. Cover Image */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel4')}
                    onChange={handleAccordionChange('panel4')}
                    sx={{
                        mb: 2,
                        border: hasSectionError.cover ? '2px solid #ef5350' : '1px solid #e0e0e0',
                        backgroundColor: hasSectionError.cover ? 'rgba(239, 83, 80, 0.05)' : 'transparent',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            borderColor: hasSectionError.cover ? '#ef5350' : '#e0e0e0',
                        }
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '🖼️ صورة الغلاف' : '🖼️ Cover Image'}
                        </Typography>
                        <Fade in={hasSectionError.cover} timeout={300}>
                            <Typography
                                sx={{
                                    ml: 1,
                                    color: '#ef5350',
                                    fontSize: '0.85rem',
                                    fontWeight: 600
                                }}
                            >
                                {isArabic ? '⚠️ يوجد أخطاء' : '⚠️ Errors'}
                            </Typography>
                        </Fade>
                    </AccordionSummary>
                    <AccordionDetails>
                        <CoverImageSection
                            {...commonProps}
                            handleCoverImageChange={handleCoverImageChange}
                            isUploadingCover={isUploadingCover}
                            isEdit={isEdit}
                        />
                    </AccordionDetails>
                </Accordion>
            </Box>
        </Paper>
    );
};

export default Step0ProgramInfo;
